import React, { useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Icon, Input, InputNumber, Modal, Progress,
  Radio, Row, Select, Statistic, Tag, message,
} from 'antd';
import moment from 'moment';

import {
  addMissingFromAssetPro, clearEntryReview, deleteEntryReviewById, upsertEntryReview,
} from './registerDB';
import { SITE_TYPE, VERDICT, VERDICT_COLOR, VERDICT_LABEL } from './reconEngine';
import { ExportableTable, downloadCsv, fmtL, numSorter, searchProps, strSorter } from '../tableKit';

const { Option } = Select;
const { TextArea } = Input;

/*
 * Marking off the entries.
 *
 * Every AssetPro entry for the period, with one button to say it agrees with the
 * register and one to say what is wrong with it. Each correction carries a signed
 * effect on the balance, applied from its own date forward — so fixing an entry
 * five days back pulls the closing balance, and every day after it, into line.
 *
 * A correction records what the entry SHOULD say. It does not change the AssetPro
 * value shown here: this module has no write access to fuel records, and showing
 * a corrected number as though it were the system's would hide the discrepancy.
 * Corrections leave as a worklist to action in AssetPro.
 */

const money = (v) => (v === null || v === undefined ? '—' : fmtL(v));
const L = (cl) => (cl === null || cl === undefined ? null : cl / 100);

// The options offered depend on the entry. A "wrong source" only makes sense one
// way round for any given entry — a direct fill can only turn out to have come
// from a tank, and a tank issue can only turn out to have been direct — so the
// direction is implied rather than asked for.
const fixableFor = (entryKind, hasTanks) => {
  const base = [
    VERDICT.NOT_IN_REGISTER, VERDICT.QTY_WRONG, VERDICT.WRONG_ASSET,
    VERDICT.WRONG_DATE, VERDICT.DUPLICATE,
  ];
  if (!hasTanks) return base;
  if (entryKind === 'DIRECT' || entryKind === 'ISSUE') return base.concat(VERDICT.WRONG_SOURCE);
  return base;
};

const sourceLabelFor = (entryKind) => (entryKind === 'DIRECT'
  ? 'Recorded as a direct purchase, but it was issued from a tank'
  : 'Recorded as issued from the tank, but it was a direct purchase');

function EntryReviewPanel({
  entries, summary, worklist, recon, periodKey, scopeKey, periodFrom, periodTo,
  storages, assetChoices = [], directCount, dateFocus, kindFocus, focusProgress, onChanged,
}) {
  // ALL, not UNREVIEWED. Marking an entry OK used to drop it straight out of the
  // list, which reads as the work being lost — and leaves no way to see what has
  // already been checked, or to undo a wrong call. Everything stays put; the
  // verdict column is what changes.
  const [filter, setFilter] = useState('ALL');
  const [dayFilter, setDayFilter] = useState(null);
  const [kindFilter, setKindFilter] = useState('ALL');
  const [editing, setEditing] = useState(null);
  const [addMissing, setAddMissing] = useState(null);

  // When the day-wise panel sends the reviewer here for a specific day, narrow to
  // it and widen the verdict filter, so what they were sent to look at is on screen.
  const focusRef = useRef(null);
  if (dateFocus !== undefined && focusRef.current !== dateFocus) {
    focusRef.current = dateFocus;
    setDayFilter(dateFocus);
    setFilter('ALL');
    // Sent here from the direct-fuel day-wise? Then it is the direct fills that
    // need auditing, not the tank issues that happen to share the date.
    if (dateFocus) setKindFilter(kindFocus === 'DIRECT' ? 'DIRECT' : 'ALL');
  }

  const rows = useMemo(() => {
    const all = summary.reviewed.concat(summary.unreviewed).map(({ entry, review, verdict }) => ({
      key: entry.key,
      entry,
      review,
      verdict,
      date: entry.dayKey,
      kind: entry.entryKindLabel,
      entryKind: entry.entryKind,
      assetCode: entry.assetCode,
      assetName: entry.assetName,
      storageName: entry.storageName,
      quantity: entry.quantity,
      voucherNo: entry.voucherNo,
      correctQuantityL: review ? review.correctQuantityL : null,
      correctDate: review ? review.correctDate : null,
      correctAssetCode: review ? review.correctAssetCode : null,
      note: review ? review.note : null,
    }));
    const byDay = dayFilter ? all.filter((r) => r.date === dayFilter) : all;
    const scoped = kindFilter === 'ALL' ? byDay : byDay.filter((r) => r.entryKind === kindFilter);
    if (filter === 'ALL') return scoped;
    if (filter === 'UNREVIEWED') return scoped.filter((r) => !r.verdict);
    if (filter === 'FLAGGED') return scoped.filter((r) => r.verdict && r.verdict !== VERDICT.OK);
    return scoped.filter((r) => r.verdict === filter);
  }, [summary, filter, dayFilter, kindFilter]);

  // Only the types actually present are offered, so a no-tank site does not get a
  // "Receipt" button that filters to nothing.
  const kindCounts = useMemo(() => {
    const order = [['ISSUE', 'Issues'], ['RECEIPT', 'Receipts'], ['DIRECT', 'Direct']];
    const all = summary.reviewed.concat(summary.unreviewed);
    return order
      .map(([kind, label]) => ({
        kind,
        label,
        n: all.filter(({ entry }) => entry.entryKind === kind).length,
      }))
      .filter((k) => k.n > 0);
  }, [summary]);

  const mark = (row, verdict) => {
    try {
      upsertEntryReview({
        periodKey,
        scopeKey,
        entryKey: row.key,
        entryId: row.entry.id,
        kind: row.entry.entryKind,
        storageId: row.entry.storageId,
        date: row.date,
        assetCode: row.assetCode,
        quantityL: row.quantity,
        verdict,
      });
      onChanged();
    } catch (e) {
      message.error(e.message);
    }
  };

  const saveCorrection = () => {
    const { row, verdict, correctQuantityL, correctDate, note } = editing;
    if (verdict === VERDICT.QTY_WRONG && (correctQuantityL === null || correctQuantityL === undefined)) {
      message.error('Enter the quantity the register shows — without it the difference cannot be measured.');
      return;
    }
    if (verdict === VERDICT.WRONG_DATE && !correctDate) {
      message.error('Pick the date the register shows.');
      return;
    }
    if (verdict === VERDICT.WRONG_ASSET && !editing.correctAssetId) {
      message.error('Pick the machine the register names — without it there is nothing to act on.');
      return;
    }
    if (verdict === VERDICT.WRONG_SOURCE && row.entry.entryKind === 'DIRECT' && !editing.correctStorageId) {
      message.error('Pick the tank it was issued from — without it the fuel cannot be put back on the right tank.');
      return;
    }
    try {
      const tank = storages.find((st) => st.storageId === editing.correctStorageId);
      const asset = assetChoices.find((a) => a.id === editing.correctAssetId);
      upsertEntryReview({
        periodKey,
        scopeKey,
        entryKey: row.key,
        entryId: row.entry.id,
        kind: row.entry.entryKind,
        storageId: row.entry.storageId,
        date: row.date,
        assetCode: row.assetCode,
        quantityL: row.quantity,
        verdict,
        correctQuantityL: verdict === VERDICT.QTY_WRONG ? correctQuantityL : null,
        correctDate: verdict === VERDICT.WRONG_DATE && correctDate ? moment(correctDate).format('YYYY-MM-DD') : null,
        correctStorageId: verdict === VERDICT.WRONG_SOURCE && row.entry.entryKind === 'DIRECT'
          ? editing.correctStorageId : null,
        correctStorageCode: verdict === VERDICT.WRONG_SOURCE && tank ? tank.assetCode : null,
        correctAssetId: verdict === VERDICT.WRONG_ASSET ? editing.correctAssetId : null,
        correctAssetCode: verdict === VERDICT.WRONG_ASSET && asset ? asset.assetCode : null,
        note: (note || '').trim() || null,
      });
      message.success('Recorded.');
      setEditing(null);
      onChanged();
    } catch (e) {
      message.error(e.message);
    }
  };

  const saveMissing = () => {
    const m = addMissing;
    if (m.quantityL === null || m.quantityL === undefined || m.quantityL <= 0) {
      message.error('Enter the litres the register shows.');
      return;
    }
    try {
      addMissingFromAssetPro({
        periodKey,
        scopeKey,
        storageId: m.storageId || null,
        date: moment(m.date).format('YYYY-MM-DD'),
        assetCode: m.assetCode || null,
        quantityL: m.quantityL,
        kind: m.kind,
        note: (m.note || '').trim() || null,
      });
      message.success('Recorded.');
      setAddMissing(null);
      onChanged();
    } catch (e) {
      message.error(e.message);
    }
  };

  const exportWorklist = () => {
    if (!worklist.length) { message.warning('Nothing to correct.'); return; }
    downloadCsv(
      `assetpro-corrections-${periodFrom}_${periodTo}.csv`,
      ['Date', 'Type', 'Voucher', 'Entry id', 'Asset', 'Tank', 'AssetPro litres', 'Finding', 'What to do', 'Register litres', 'Correct date', 'Effect on balance (L)', 'Note', 'Reviewed by'],
      worklist.map((w) => [
        w.date, w.kind, w.voucherNo, w.entryId, w.assetCode, w.storageName, w.currentQtyL,
        w.verdictLabel, w.action, w.correctQuantityL, w.correctDate, w.impactL, w.note, w.reviewedBy,
      ])
    );
  };

  const columns = [
    { title: 'Date', dataIndex: 'date', width: 115, sorter: strSorter('date'), defaultSortOrder: 'descend', ...searchProps('date') },
    { title: 'Type', dataIndex: 'kind', width: 130, render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Machine',
      dataIndex: 'assetCode',
      width: 170,
      ...searchProps('assetCode'),
      render: (v, r) => (
        <div>
          <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span>
          {r.assetName && <div style={{ fontSize: 11, color: '#999' }}>{r.assetName}</div>}
        </div>
      ),
    },
    {
      title: 'Litres',
      dataIndex: 'quantity',
      width: 120,
      align: 'right',
      sorter: numSorter('quantity'),
      render: (v, r) => (
        <div>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{money(v)}</span>
          {r.correctQuantityL !== null && r.correctQuantityL !== undefined && (
            <div style={{ fontSize: 11, color: '#f5222d' }}>register: {money(r.correctQuantityL)}</div>
          )}
        </div>
      ),
    },
    { title: 'Voucher', dataIndex: 'voucherNo', width: 120, render: (v) => v || '—' },
    {
      title: 'Verdict',
      dataIndex: 'verdict',
      width: 200,
      render: (v, r) => (v
        ? (
          <span>
            <Tag color={VERDICT_COLOR[v]}>{VERDICT_LABEL[v]}</Tag>
            {r.correctAssetCode && <div style={{ fontSize: 11, color: '#999' }}>→ {r.correctAssetCode}</div>}
            {r.correctDate && <div style={{ fontSize: 11, color: '#999' }}>→ {r.correctDate}</div>}
            {r.note && <div style={{ fontSize: 11, color: '#999' }}>{r.note}</div>}
          </span>
        )
        : <Tag>not checked</Tag>),
    },
    {
      title: 'Against the register',
      key: 'act',
      width: 260,
      render: (v, r) => (
        <div>
          <Button size="small" type={r.verdict === VERDICT.OK ? 'primary' : 'default'} icon="check" onClick={() => mark(r, VERDICT.OK)}>OK</Button>
          <Button
            size="small"
            style={{ marginLeft: 6 }}
            onClick={() => setEditing({
              row: r,
              verdict: r.verdict && r.verdict !== VERDICT.OK ? r.verdict : VERDICT.QTY_WRONG,
              correctQuantityL: r.correctQuantityL,
              correctDate: r.correctDate ? moment(r.correctDate) : null,
              // With one tank there is nothing to choose, so it is filled in.
              correctStorageId: (r.review && r.review.correctStorageId)
                || (storages.length === 1 ? storages[0].storageId : null),
              correctAssetId: (r.review && r.review.correctAssetId) || null,
              note: r.note || '',
            })}
          >
            Correct…
          </Button>
          {r.verdict && (
            <Button size="small" style={{ marginLeft: 6 }} onClick={() => { clearEntryReview(periodKey, r.key); onChanged(); }}>Undo</Button>
          )}
        </div>
      ),
    },
  ];

  const c = summary.counts;
  const pct = Math.round(summary.progress * 100);

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ minWidth: 200 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Checked</div>
            <Progress percent={pct} status={pct === 100 ? 'success' : 'active'} />
            <div style={{ fontSize: 12, color: '#999' }}>{c.reviewed} of {c.total}</div>
          </div>
          <Statistic title="Agree" value={c.ok} valueStyle={{ fontSize: 20, color: '#52c41a' }} />
          <Statistic title="Flagged" value={c.flagged} valueStyle={{ fontSize: 20, color: c.flagged ? '#f5222d' : undefined }} />
          <span style={{ flex: 1 }} />
          <Button
            icon="plus"
            onClick={() => setAddMissing({
              date: moment(dayFilter || periodTo),
              storageId: (storages[0] || {}).storageId,
              assetCode: '',
              quantityL: null,
              kind: 'ISSUE',
              note: '',
            })}
          >
            Register has one AssetPro is missing
          </Button>
          <Button type="primary" icon="download" onClick={exportWorklist} disabled={!worklist.length}>
            Correction worklist ({worklist.length})
          </Button>
        </div>
      </Card>

      {recon.state === 'RECONCILED' && recon.reconciledByCorrections && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message="Your corrections have closed the difference"
          description="The balance now reconciles with these corrections applied. They still need actioning in AssetPro — download the worklist above."
        />
      )}

      {recon.siteType === SITE_TYPE.MULTI_STORAGE && directCount > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message={`${directCount} direct fill(s) are not listed here`}
          description="This location has more than one tank, so fuel bought and issued straight to a machine is reconciled as its own total on the Balances tab rather than entry by entry."
        />
      )}

      {recon.siteType === SITE_TYPE.SINGLE_STORAGE && directCount > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message={`${directCount} direct fill(s) are included below`}
          description="The register records the direct purchase and its issue together, so they are checked off from the same page. Correcting one will not move the bowser balance — that fuel never went through the tank — but it still belongs on the worklist."
        />
      )}

      {dayFilter && (
        focusProgress && focusProgress.startedOut ? (
          <Alert
            type={focusProgress.reconciled ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 10 }}
            message={focusProgress.reconciled
              ? `${dayFilter} now reconciles — you can stop here`
              : `${dayFilter}: ${money(L(focusProgress.remainingCl))} L still to find`}
            description={
              <div>
                <div style={{ marginBottom: 6 }}>
                  This day was out by <b>{money(L(focusProgress.toFindCl))} L</b>.
                  {' '}Your corrections so far account for <b>{money(L(focusProgress.accountedCl))} L</b>.
                  {focusProgress.reconciled
                    ? ' Nothing is left to explain, so the remaining entries on this day do not need checking.'
                    : ` ${money(L(focusProgress.remainingCl))} L is still unexplained.`}
                </div>
                <Button size="small" onClick={() => { setDayFilter(null); setFilter('UNREVIEWED'); }}>
                  Show the whole period
                </Button>
              </div>
            }
          />
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 10 }}
            message={`Showing ${dayFilter} only`}
            action={<Button size="small" onClick={() => { setDayFilter(null); setFilter('UNREVIEWED'); }}>Whole period</Button>}
          />
        )
      )}

      {summary.extra.length > 0 && (
        <Card size="small" style={{ marginBottom: 10 }} title={`Recorded as missing from AssetPro (${summary.extra.length})`}>
          {summary.extra.map((r) => (
            <div key={r._id} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
              <span style={{ width: 110 }}>{r.date}</span>
              <span style={{ width: 140, fontFamily: 'monospace' }}>{r.assetCode || '—'}</span>
              <span style={{ width: 110, textAlign: 'right', fontFamily: 'monospace' }}>{money(r.quantityL)} L</span>
              <span style={{ flex: 1, color: '#999', paddingLeft: 12 }}>{r.note || ''}</span>
              <Icon type="delete" style={{ color: '#f5222d', cursor: 'pointer' }} onClick={() => { deleteEntryReviewById(r._id); onChanged(); }} />
            </div>
          ))}
        </Card>
      )}

      <div style={{ marginBottom: 10, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Radio.Group size="small" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <Radio.Button value="ALL">All ({c.total})</Radio.Button>
          <Radio.Button value="UNREVIEWED">Not checked ({c.unreviewed})</Radio.Button>
          <Radio.Button value="FLAGGED">Flagged ({c.flagged})</Radio.Button>
          <Radio.Button value="OK">OK ({c.ok})</Radio.Button>
        </Radio.Group>

        {kindCounts.length > 1 && (
          <Radio.Group size="small" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <Radio.Button value="ALL">Every type</Radio.Button>
            {kindCounts.map((k) => (
              <Radio.Button key={k.kind} value={k.kind}>{k.label} ({k.n})</Radio.Button>
            ))}
          </Radio.Group>
        )}

        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: '#999' }}>
          Checked entries stay on the list. Marking one never changes its AssetPro
          value — corrections leave as a worklist.
        </span>
      </div>

      <ExportableTable
        columns={columns}
        dataSource={rows}
        rowKey="key"
        size="small"
        scroll={{ x: 1160, y: 520 }}
        pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
        exportName={`entries-${periodFrom}_${periodTo}.csv`}
        exportHeader={['Date', 'Type', 'Machine', 'AssetPro litres', 'Voucher', 'Verdict', 'Register litres', 'Correct date', 'Note']}
        exportRow={(r) => [r.date, r.kind, r.assetCode, r.quantity, r.voucherNo, r.verdict ? VERDICT_LABEL[r.verdict] : 'not checked', r.correctQuantityL, r.correctDate, r.note]}
      />

      <Modal
        title={editing ? `What does the register say? — ${editing.row.assetCode || ''} on ${editing.row.date}` : ''}
        visible={!!editing}
        onCancel={() => setEditing(null)}
        onOk={saveCorrection}
        okText="Record"
        width={600}
      >
        {editing && (
          <>
            <div style={{ marginBottom: 10, padding: 10, background: '#fafafa', borderRadius: 4 }}>
              AssetPro records <b style={{ fontFamily: 'monospace' }}>{money(editing.row.quantity)} L</b>
              {editing.row.assetCode ? <> to <b>{editing.row.assetCode}</b></> : null} on <b>{editing.row.date}</b>.
            </div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>What is wrong with it?</div>
            <Select style={{ width: '100%' }} value={editing.verdict} onChange={(v) => setEditing({ ...editing, verdict: v })}>
              {fixableFor(editing.row.entry.entryKind, storages.length > 0).map((v) => (
                <Option key={v} value={v}>
                  {v === VERDICT.WRONG_SOURCE ? sourceLabelFor(editing.row.entry.entryKind) : VERDICT_LABEL[v]}
                </Option>
              ))}
            </Select>

            {editing.verdict === VERDICT.QTY_WRONG && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#888' }}>Litres per the register</div>
                <InputNumber style={{ width: '100%' }} value={editing.correctQuantityL} onChange={(v) => setEditing({ ...editing, correctQuantityL: v })} />
              </div>
            )}

            {editing.verdict === VERDICT.WRONG_ASSET && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#888' }}>Machine the register names</div>
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="children"
                  placeholder="start typing an asset code"
                  value={editing.correctAssetId || undefined}
                  onChange={(v) => setEditing({ ...editing, correctAssetId: v })}
                >
                  {assetChoices.map((a) => (
                    <Option key={a.id} value={a.id}>
                      {a.assetCode}{a.altName ? ` — ${a.altName}` : ''}
                    </Option>
                  ))}
                </Select>
                <Alert
                  style={{ marginTop: 8 }}
                  type="info"
                  showIcon
                  message="This will not change the balance"
                  description="The quantity is right and the fuel left the tank either way, so no difference closes. It is recorded because consumption is overstated for one machine and understated for the other until the entry is re-posted."
                />
              </div>
            )}

            {editing.verdict === VERDICT.WRONG_SOURCE && (
              <div style={{ marginTop: 12 }}>
                {editing.row.entry.entryKind === 'DIRECT' ? (
                  <>
                    <div style={{ fontSize: 12, color: '#888' }}>Which tank was it issued from?</div>
                    <Select
                      style={{ width: '100%' }}
                      value={editing.correctStorageId || (storages.length === 1 ? storages[0].storageId : undefined)}
                      onChange={(v) => setEditing({ ...editing, correctStorageId: v })}
                      placeholder="pick the tank"
                    >
                      {storages.map((st) => (
                        <Option key={st.storageId} value={st.storageId}>{st.assetCode} — {st.storageName}</Option>
                      ))}
                    </Select>
                  </>
                ) : null}
                <Alert
                  style={{ marginTop: 8 }}
                  type="info"
                  showIcon
                  message="This moves fuel between the tank and the direct total"
                  description={editing.row.entry.entryKind === 'DIRECT'
                    ? `The machine got its ${money(editing.row.quantity)} L either way, so the total issued does not change — but the tank actually gave out that much more, so its closing balance comes DOWN by ${money(editing.row.quantity)} L.`
                    : `The machine got its ${money(editing.row.quantity)} L either way, so the total issued does not change — but the tank never gave it out, so its closing balance goes UP by ${money(editing.row.quantity)} L.`}
                />
              </div>
            )}

            {editing.verdict === VERDICT.WRONG_DATE && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#888' }}>Date per the register</div>
                <DatePicker style={{ width: '100%' }} value={editing.correctDate} onChange={(d) => setEditing({ ...editing, correctDate: d })} />
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  A date inside this period only moves the entry between days and changes no total. A
                  date outside it takes the fuel out of this period, and does.
                </div>
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: '#888' }}>Note — what you checked, and against what</div>
              <TextArea rows={2} value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} />
            </div>
          </>
        )}
      </Modal>

      <Modal
        title="The register records a movement AssetPro does not"
        visible={!!addMissing}
        onCancel={() => setAddMissing(null)}
        onOk={saveMissing}
        okText="Record"
        width={600}
      >
        {addMissing && (
          <>
            <Row gutter={12}>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#888' }}>Date</div>
                <DatePicker style={{ width: '100%' }} value={addMissing.date} onChange={(d) => setAddMissing({ ...addMissing, date: d })} />
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#888' }}>Type</div>
                <Select style={{ width: '100%' }} value={addMissing.kind} onChange={(v) => setAddMissing({ ...addMissing, kind: v })}>
                  <Option value="ISSUE">Issue to a machine</Option>
                  {storages.length > 0 && <Option value="RECEIPT">Receipt into the tank</Option>}
                  <Option value="DIRECT">Direct purchase</Option>
                </Select>
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#888' }}>Litres</div>
                <InputNumber style={{ width: '100%' }} value={addMissing.quantityL} onChange={(v) => setAddMissing({ ...addMissing, quantityL: v })} />
              </Col>
            </Row>
            <Row gutter={12} style={{ marginTop: 10 }}>
              {storages.length > 0 && (
                <Col span={12}>
                  <div style={{ fontSize: 12, color: '#888' }}>Tank</div>
                  <Select style={{ width: '100%' }} value={addMissing.storageId} onChange={(v) => setAddMissing({ ...addMissing, storageId: v })}>
                    {storages.map((s) => <Option key={s.storageId} value={s.storageId}>{s.assetCode}</Option>)}
                  </Select>
                </Col>
              )}
              <Col span={storages.length > 0 ? 12 : 24}>
                <div style={{ fontSize: 12, color: '#888' }}>Machine, as written on the register</div>
                <Input value={addMissing.assetCode} onChange={(e) => setAddMissing({ ...addMissing, assetCode: e.target.value })} />
              </Col>
            </Row>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: '#888' }}>Note</div>
              <TextArea rows={2} value={addMissing.note} onChange={(e) => setAddMissing({ ...addMissing, note: e.target.value })} />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default EntryReviewPanel;
